import {
    CATEGORIES,
    QUESTIONS,
    isNumericAnswer,
} from "../data/diagnosticQuestions";

const READINESS_CATEGORIES = [
    "stefna",
    "markmid",
    "umhverfi",
    "mannaudur",
    "breyting",
];
const PRESSURE_CATEGORIES = ["samkeppni", "imynd"];
const OPPORTUNITY_CATEGORY = "soun";

export function calculateCategoryScores(answers) {
    const scores = {};
    for (const cat of CATEGORIES) {
        const catQuestions = QUESTIONS.filter((q) => q.categoryId === cat.id);
        const catAnswers = catQuestions
            .map((q) => answers[q.id])
            .filter(isNumericAnswer);
        if (catAnswers.length === 0) {
            scores[cat.id] = null;
            continue;
        }
        const sum = catAnswers.reduce((a, b) => a + b, 0);
        scores[cat.id] = Math.round((sum / catAnswers.length) * 10) / 10;
    }
    return scores;
}

export function calculateCompositeScores(categoryScores) {
    const readinessValues = READINESS_CATEGORIES.map(
        (id) => categoryScores[id],
    ).filter((v) => v != null);

    const pressureValues = PRESSURE_CATEGORIES.map(
        (id) => categoryScores[id],
    ).filter((v) => v != null);

    const opportunityValue = categoryScores[OPPORTUNITY_CATEGORY];

    const avg = (arr) => {
        if (arr.length === 0) return null;
        return (
            Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10
        );
    };

    return {
        grunnstada: avg(readinessValues),
        thrystingur: avg(pressureValues),
        taekifaeri: opportunityValue,
    };
}

export function recommendPackage(composite) {
    const { grunnstada, thrystingur, taekifaeri } = composite;

    if (grunnstada == null || thrystingur == null || taekifaeri == null) {
        return "none";
    }

    if (taekifaeri >= 4.0 && thrystingur >= 3.5) {
        return "dafna";
    }
    if (grunnstada >= 4.0 && thrystingur >= 4.0 && taekifaeri >= 3.5) {
        return "dafna";
    }

    if (grunnstada >= 3.5 && thrystingur >= 3.0 && taekifaeri >= 3.0) {
        return "efla";
    }
    if (grunnstada >= 3.0 && taekifaeri >= 3.5 && thrystingur >= 2.5) {
        return "efla";
    }

    if (grunnstada >= 2.5 && thrystingur >= 2.5) {
        return "vaxa";
    }

    if (grunnstada >= 1.5 && (thrystingur >= 2.0 || taekifaeri >= 2.0)) {
        return "kveikja";
    }

    return "none";
}

export function generateInsights(categoryScores, composite, lang = "is") {
    const insights = [];
    const catEntries = CATEGORIES.map((c) => ({
        ...c,
        score: categoryScores[c.id],
    })).filter((c) => c.score != null);

    if (catEntries.length === 0) return insights;

    const readinessCats = catEntries.filter((c) =>
        READINESS_CATEGORIES.includes(c.id),
    );
    const strongest =
        readinessCats.length > 0
            ? readinessCats.reduce((a, b) => (a.score >= b.score ? a : b))
            : null;
    const weakest =
        readinessCats.length > 0
            ? readinessCats.reduce((a, b) => (a.score <= b.score ? a : b))
            : null;

    if (strongest) {
        insights.push({
            type: "strongest",
            icon: "▲",
            title_is: `Sterkasta sviðið: ${strongest.name_is}`,
            title_en: `Strongest area: ${strongest.name_en}`,
            desc_is: `Þið standið sterkast í ${strongest.name_is.toLowerCase()} (${strongest.score}). Þetta er traustur grunnur sem hægt er að byggja á.`,
            desc_en: `You are strongest in ${strongest.name_en.toLowerCase()} (${strongest.score}). This is a solid foundation to build on.`,
        });
    }

    if (weakest && composite.taekifaeri != null) {
        insights.push({
            type: "opportunity",
            icon: "◆",
            title_is: `Stærsta tækifærið: ${weakest.name_is}`,
            title_en: `Biggest opportunity: ${weakest.name_en}`,
            desc_is: `Stærsta tækifærið liggur í ${weakest.name_is.toLowerCase()} (${weakest.score}). Hér getur markviss vinna skilað mestum árangri.`,
            desc_en: `The biggest opportunity lies in ${weakest.name_en.toLowerCase()} (${weakest.score}). Focused work here can deliver the greatest results.`,
        });
    }

    const pressureAvg = composite.thrystingur;
    const opportunityScore = composite.taekifaeri;

    const conditionalInsights = [];

    if (pressureAvg != null && pressureAvg > 3.5) {
        conditionalInsights.push({
            type: "pressure",
            icon: "⚠",
            priority: pressureAvg,
            title_is: `Ytri þrýstingur er mikill (${pressureAvg})`,
            title_en: `External pressure is high (${pressureAvg})`,
            desc_is:
                "Viðskiptavinir, regluverk og markaður krefjast aðgerða. Brýnt er að bregðast við tímanlega.",
            desc_en:
                "Customers, regulations, and the market demand action. It is urgent to respond in a timely manner.",
        });
    }

    if (opportunityScore != null && opportunityScore > 3.5) {
        conditionalInsights.push({
            type: "operational_roi",
            icon: "↗",
            priority: opportunityScore,
            title_is: `Sterk rekstrartækifæri (${opportunityScore})`,
            title_en: `Strong operational opportunities (${opportunityScore})`,
            desc_is:
                "Sterk tækifæri virðast vera til að draga úr sóun og bæta nýtingu í rekstri.",
            desc_en:
                "There appear to be strong opportunities to reduce waste and improve operational efficiency.",
        });
    }

    const mannaudur = categoryScores["mannaudur"];
    const breyting = categoryScores["breyting"];
    if (
        mannaudur != null &&
        breyting != null &&
        Math.abs(mannaudur - breyting) >= 1.5
    ) {
        conditionalInsights.push({
            type: "people_gap",
            icon: "○",
            priority: Math.abs(mannaudur - breyting),
            title_is: "Mannauður og breytingargeta eru ójöfn",
            title_en: "HR and change capacity are uneven",
            desc_is:
                "Tækifæri í að styrkja bæði mannauð og getu til breytinga saman.",
            desc_en:
                "Opportunity to strengthen both HR and change capacity together.",
        });
    }

    if (breyting != null && catEntries.every((c) => c.score <= breyting)) {
        conditionalInsights.push({
            type: "change_ready",
            icon: "✓",
            priority: breyting,
            title_is: "Breytingavilji er til staðar",
            title_en: "Willingness to change exists",
            desc_is: `Starfsfólkið virðist vera tilbúið — nú þarf verkfærin til að styðja við þau.`,
            desc_en: `Staff appear to be ready — now the tools are needed to support them.`,
        });
    }

    conditionalInsights.sort((a, b) => b.priority - a.priority);
    if (conditionalInsights.length > 0) {
        insights.push(conditionalInsights[0]);
    }

    return insights;
}

export function getPackageText(pkg, lang = "is") {
    const texts = {
        dafna: {
            title_is: "Dafna — heildstæðasta lausnin",
            title_en: "Dafna — the most comprehensive solution",
            why_is: "Fyrirtækið ykkar þarf stjórnkerfi, ekki bara verkfæri. Margar einingar, gæðakröfur og formlegt kolefnisbókhald kalla á Dafna — heildstæðustu lausn Laufið.",
            why_en: "Your company needs a management system, not just tools. Multiple entities, quality requirements, and formal carbon accounting call for Dafna — Laufið's most comprehensive solution.",
        },
        efla: {
            title_is: "Efla — reksturinn styrktur",
            title_en: "Efla — operations strengthened",
            why_is: "Fyrirtækið ykkar er tilbúið í raungögn og dýpri yfirsýn. Efla tengir ykkur við Kolka-raungögn, veitir eigna-app fyrir fasteignastjóra, HR-mælaborð og ítarlegan kolefnisútreikning.",
            why_en: "Your company is ready for real data and deeper insights. Efla connects you to Kolka live data, provides a property manager app, HR dashboard, and detailed carbon calculations.",
        },
        vaxa: {
            title_is: "Vaxa — vöxtur og innleiðing",
            title_en: "Vaxa — growth and implementation",
            why_is: "Þið eruð komin með traustan grunn og viðskiptavinir ykkar eru farnir að spyrja spurninga. Vaxa bætir við birgjamat, skýrslum, starfsmannakönnunum og fjármálayfirliti.",
            why_en: "You have built a solid foundation and your clients are starting to ask questions. Vaxa adds supplier assessment, reports, employee surveys, and financial overview.",
        },
        kveikja: {
            title_is: "Kveikja — fyrstu skrefin tekin",
            title_en: "Kveikja — first steps taken",
            why_is: "Þetta er góður byrjunarstaður. Kveikja gefur ykkur 120+ aðgerðir, kolefnisreiknir, markmið og fullt mælaborð — allt sem þarf til að hefja vegferðina.",
            why_en: "This is a great starting point. Kveikja gives you 120+ actions, carbon calculator, goals, and full dashboard — everything needed to start the journey.",
        },
        none: {
            title_is: "Byrjið frítt og skoðið síðar",
            title_en: "Start free and explore later",
            why_is: "Samkvæmt svörunum virðist þörfin ekki vera komin á yfirborðið enn — en það er aldrei of snemma. Byrjið frítt með 40 skref og Laufaskólann, og skoðið aftur síðar.",
            why_en: "Based on your answers, the need hasn't surfaced yet — but it's never too early. Start free with 40 steps and Laufaskólinn, and revisit later.",
        },
    };
    return texts[pkg] || texts.none;
}

export function calculateQuizScore(categoryScores) {
    const values = Object.values(categoryScores).filter((v) => v != null);
    if (values.length === 0) return 0;
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return Math.round(avg * 20);
}

export function buildFullResult(answers) {
    const categoryScores = calculateCategoryScores(answers);
    const composite = calculateCompositeScores(categoryScores);
    const recommendation = recommendPackage(composite);
    const insights = generateInsights(categoryScores, composite);
    const quizScore = calculateQuizScore(categoryScores);
    const packageText = getPackageText(recommendation);

    return {
        categoryScores,
        composite,
        recommendation,
        insights,
        quizScore,
        packageText,
        answers,
    };
}
